import { connect, FieldArray } from 'formik'
// @ts-expect-error
import { buildYup } from 'json-schema-to-yup'
import { cloneDeep, flatten, get, has, sortBy } from 'lodash'
import { useEffect, useRef, useState } from 'react'
import { IntlShape, useIntl } from 'react-intl'
import { Popup } from 'semantic-ui-react'
import styled from 'styled-components'
import { v4 } from 'uuid'
import { array, lazy, mixed, object, string } from 'yup'

import Button from '@/components/Button'
import FilterSelect from '@/components/Form/FilterSelect'
import { AddCircle, Delete, Info } from '@/components/Icons'
import { FormLabel, Row } from '@/components/StyledComponents'
import { BlockBorder, BlockTitle, StyledBlockTitleBar } from '@/pages/SpaceAppBuilder/components/StyledComponents'
import SuggestionBox, { SuggestionTypes } from '@/pages/SpaceAppBuilder/components/SuggestionBox/SuggestionBox'
import AutoCompleteVariableInput from '@/pages/SpaceAppBuilder/components/VariableInputDropdown/AutoCompleteVariableInput'
import messages from '@/pages/SpaceAppBuilder/FeaturesPage/Operations/messages'
import { getDataTransformActionConfig } from '@/pages/SpaceAppBuilder/FeaturesPage/utils'
import { useWorkflowFeature } from '@/pages/SpaceAppBuilder/FeaturesPage/WorkflowFeatureContext'
import {
  ActionConfigProps,
  DataTransformAction,
  DataTransformStepData,
  FieldTypes,
  StepData,
  Variable
} from '@/pages/SpaceAppBuilder/utilities/interfaces'
import { getAllFlattenedVariables } from '@/pages/SpaceAppBuilder/utilities/variableHelpers'

import actionsConfig from './actionsConfig.json'
import ActionTooltip from './ActionTooltip'
import ParametersForm from './ParametersForm'

//<editor-fold desc="Styled Components">
const StyledButton = styled(Button)`
  display: flex;
  align-items: center;
  margin-left: 0;

  svg {
    margin-right: 0.2rem;
  }
`
const StyledFilterSelect = styled(FilterSelect)`
  .ui.selection.dropdown .menu > .item {
    padding: 0.5rem 1rem !important;
  }
`
export const Description = styled.p`
  margin-top: 2px;
  margin-bottom: 5px;
  margin-right: 5px;
  font-size: 0.8rem;
  word-break: break-word;
  white-space: normal;
  color: #6c6c6c;
`
const StyledRow = styled(Row)`
  justify-content: flex-start;
  margin-bottom: 5px;
`
const StyledContents = styled.div`
  flex: 1;
`
const StyledDataTransformContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: calc(100% - 48px);
`
const StyledBlockTitle = styled(BlockTitle)`
  font-size: 0.8rem;
`
const OptionLabel = styled.label`
  font-size: 14px;
  font-weight: normal;
  color: #3a3a3a;
`
const StyledTooltip = styled.div`
  display: flex;
  margin-bottom: 10px;
  cursor: pointer;

  svg,
  g {
    fill: #6c6c6c;
  }
`

const StyledActionUnavailable = styled.div`
  background-color: #f0f0f0;
  padding: 10px 15px;
  border-radius: 8px;
`

const StyledActionUnavailableText = styled.div`
  font-weight: bold;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  font-size: 14px;
`
//</editor-fold>

//<editor-fold desc="Interfaces">
type DataTransformProps = {
  readOnly?: boolean
  formik?: any
}
//</editor-fold>

const getInitialValues = (stepData?: DataTransformStepData): StepData => ({
  actionName: stepData?.actionName,
  inputVariable: stepData?.inputVariable ?? '',
  actions: stepData?.actions ?? []
})

const getValidationSchema = (intl: IntlShape) => {
  return {
    inputVariable: string().required(intl.formatMessage(messages.dataTransformInputVariableRequired)),
    // @ts-expect-error
    actions: lazy((actions: DataTransformAction[]) => {
      return array()
        .of(
          object().shape({
            action: string().required(intl.formatMessage(messages.dataTransformInputActionNameRequired)),
            parameters: lazy((parameter: undefined | any) => {
              const action =
                parameter &&
                actions.find(
                  (action: DataTransformAction) =>
                    action.parameters && Object.keys(action.parameters).includes(Object.keys(parameter)[0])
                )
              const actionConfig = getDataTransformActionConfig(action?.action)
              if (!actionConfig?.parametersSchema) {
                return mixed().notRequired()
              }

              const yupSchema = buildYup(actionConfig?.parametersSchema, {
                errMessages: actionConfig?.parametersSchema?.errorMessages
              })
              if (yupSchema !== undefined) {
                return yupSchema
              }
              return mixed().notRequired()
            })
          })
        )
        .min(1, intl.formatMessage(messages.dataTransformInputActionsMinLength1))
        .required(intl.formatMessage(messages.dataTransformInputActionsMinLength1))
    })
  }
}
const getDataTransformOperationHelpUrl =
  'https://help.workgrid.com/s/article/Understanding-the-Data-Transform-Utility-Step'
const DataTransformOperation = ({ readOnly, formik }: DataTransformProps) => {
  const intl = useIntl()

  const context = useWorkflowFeature()
  const variables = context.getVariablesForStep()
  const firstRender = useRef(true)
  const { actions, actionName, inputVariable } = formik.values.stepData
  const isSingleAction = actionName !== undefined

  if (isSingleAction && actions.length === 0) {
    const config = actionsConfig.find(action => actionName === action.name)
    formik.setFieldValue('stepData.actions[0]', {
      action: config?.name,
      inputType: config?.inputType
    })
  }

  const selectedVariable = getAllFlattenedVariables(flatten(variables)).find(
    variable => variable.name === inputVariable
  )
  const [isShowingSuggestionBox, setIsShowingSuggestionBox] = useState(false)

  const getActionsConfigurationByInputType = (type: string | undefined) => {
    return actionsConfig.filter(ac => ac.inputType === type) || []
  }

  const getLastActionAvailableActions = (actionIndex: number) => {
    const lastAction = getDataTransformActionConfig(actions[actionIndex - 1].action)
    return lastAction ? getActionsConfigurationByInputType(lastAction?.outputType) : []
  }

  useEffect(() => {
    if (inputVariable && !firstRender.current && !isSingleAction) {
      const availableActions = getActionsConfigurationByInputType(selectedVariable?.type)
      let modifiedActions = cloneDeep(actions)
      //check that the last action is valid for the selected variable output type
      if (getDataTransformActionConfig(actions[0]?.action)?.inputType !== selectedVariable?.type) {
        modifiedActions = []
      }
      if (availableActions.length > 0) {
        modifiedActions[0] = { id: v4(), action: '' }
      }
      formik.setFieldValue('stepData.actions', modifiedActions)
    }
    firstRender.current = false
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputVariable, isSingleAction])

  const onSelectVariable = (selectedVariable: Variable, formikName: string) => {
    formik.setFieldValue(formikName, selectedVariable.name)
  }

  const addActionBlock = (push: (obj: any) => void) => {
    push({ id: v4(), action: '' })
  }

  const getActionOptions = (action: any, actionIndex: number): any[] => {
    const availableActions =
      actionIndex <= 0
        ? getActionsConfigurationByInputType(selectedVariable?.type)
        : getLastActionAvailableActions(actionIndex)

    return sortBy(
      availableActions.map((action: any) => ({
        key: action.name,
        text: action.displayName,
        value: action?.name,
        content: (
          <Popup
            mouseEnterDelay={500}
            position="left center"
            pinned
            on={['click', 'hover']}
            disabled={!action.example}
            trigger={
              <div>
                <OptionLabel>{action.displayName}</OptionLabel>
                <Description>{action.description}</Description>
              </div>
            }
            content={<ActionTooltip actionConfig={action} />}
          />
        )
      })),
      'text'
    )
  }

  const hasAnotherAvailableAction = () => {
    if (actions.length === 0) {
      return getActionsConfigurationByInputType(selectedVariable?.type).length > 0
    } else {
      return getLastActionAvailableActions(actions.length).length > 0
    }
  }
  const hasLastAction = hasAnotherAvailableAction()

  const renderNoAvailableActionsMessage = () => {
    const hasActions = actions.length > 0 && actions.at(-1).action
    if (actions.length === 0 && selectedVariable) {
      return (
        <p>{intl.formatMessage(messages.dataTransformNoAvailableActionsForType, { type: selectedVariable?.type })}</p>
      )
    } else if (hasActions) {
      return <p>{intl.formatMessage(messages.dataTransformNoAvailableActions)}</p>
    } else {
      return null
    }
  }

  const onActionChange = async (e: { target: { value: string } }, index: number) => {
    const actionsClone = cloneDeep(actions)
    actionsClone[index].action = e.target.value
    actionsClone[index].inputType = getDataTransformActionConfig(e.target.value)?.inputType
    formik.setFieldValue('stepData.actions', actionsClone)

    if (actions.length > index + 1) {
      const nextAction = getDataTransformActionConfig(actions[index + 1]?.action)
      if (nextAction?.inputType !== e.target.value) {
        const modifiedActions = cloneDeep(actionsClone).splice(index, actions.length - 1)
        formik.setFieldValue('stepData.actions', modifiedActions)
      }
    }
  }
  const openSuggestionBox = () => {
    setIsShowingSuggestionBox(true)
  }

  const renderActionForm = (action: DataTransformAction, actionConfig: ActionConfigProps | undefined, index = 0) => {
    return (
      <div className="mt-2">
        {action.action && actionConfig?.example?.input && (
          <div style={{ height: '30px', width: '100%' }}>
            <Popup
              mouseEnterDelay={250}
              mouseLeaveDelay={250}
              position="left center"
              on="hover"
              hoverable
              trigger={
                <StyledTooltip>
                  <Description>
                    {intl.formatMessage(messages.dataTransformSeeExample, {
                      name: actionConfig?.displayName
                    })}
                  </Description>
                  <Info />
                </StyledTooltip>
              }
              content={<ActionTooltip actionConfig={actionConfig} />}
            />
          </div>
        )}
        {action.action && actionConfig?.parametersSchema && (
          <ParametersForm
            actionConfig={actionConfig}
            variables={variables}
            actionIndex={index}
            readOnly={readOnly}
            formik={formik}
          />
        )}
      </div>
    )
  }

  return variables?.length > 0 ? (
    <StyledDataTransformContainer className="mt-4">
      <StyledContents>
        <AutoCompleteVariableInput
          readOnly={readOnly}
          name="stepData.inputVariable"
          required={true}
          variables={variables}
          label={intl.formatMessage(messages.dataTransformInputLabel)}
          placeholder={intl.formatMessage(messages.dataTransformInputPlaceholder)}
          showValidationMessage={true}
          // @ts-expect-error
          onSelectOption={onSelectVariable}
          validVariableTypes={actionName === 'pick' ? [FieldTypes.ARRAY] : undefined}
        />
        <FieldArray
          name={`stepData.actions`}
          render={arrayHelpers => (
            <>
              {actions.map((action: DataTransformAction, index: number) => {
                const actionConfig = getDataTransformActionConfig(action?.action)
                return (
                  <div key={action.id}>
                    {isSingleAction ? (
                      renderActionForm(action, actionConfig)
                    ) : (
                      <BlockBorder>
                        <StyledBlockTitleBar>
                          <StyledBlockTitle>{intl.formatMessage(messages.action)}</StyledBlockTitle>
                          {!readOnly && index === actions.length - 1 && (
                            // eslint-disable-next-line jsx-a11y/no-static-element-interactions
                            <div className="icon remove" onClick={() => arrayHelpers.remove(index)}>
                              <Delete title="Delete Action" />
                            </div>
                          )}
                        </StyledBlockTitleBar>
                        <StyledFilterSelect
                          onBlur={() => {}}
                          disabled={readOnly}
                          name={`stepData.actions[${index}].action`}
                          placeholder={intl.formatMessage(messages.selectAction)}
                          options={getActionOptions(action, index)}
                          selection={true}
                          onChange={(event: any) => onActionChange(event, index)}
                          required
                          className="fill-space"
                        />
                        {renderActionForm(action, actionConfig, index)}
                      </BlockBorder>
                    )}
                  </div>
                )
              })}
              {!readOnly && hasLastAction && !isSingleAction && (
                <StyledButton color={'tertiary'} onClick={() => addActionBlock(arrayHelpers.push)}>
                  <AddCircle />
                  {intl.formatMessage(messages.dataTransformAddAction)}
                </StyledButton>
              )}
              {inputVariable?.length > 0 && !hasLastAction && !isSingleAction && renderNoAvailableActionsMessage()}
              {has(formik.errors, 'stepData.actions') &&
                !has(formik.errors, 'stepData.inputVariable') &&
                get(formik.errors, 'stepData.actions') ===
                  intl.formatMessage(messages.dataTransformInputActionsMinLength1) && (
                  <p className="field__error">{get(formik.errors, 'stepData.actions')}</p>
                )}

              {inputVariable?.length > 0 &&
                actions?.length > 0 &&
                ['object', 'array'].includes(getDataTransformActionConfig(actions.at(-1)?.action)?.outputType || '')}
            </>
          )}
        />
        {!isSingleAction && (
          <StyledActionUnavailable className="mb-6">
            {!isShowingSuggestionBox && (
              <StyledActionUnavailableText>
                {intl.formatMessage(messages.dataTransformNotSeeingAction)}{' '}
                <Button color="transparent" size="inline" onClick={openSuggestionBox}>
                  {intl.formatMessage(messages.dataTransformLetUsKnow)}
                </Button>
              </StyledActionUnavailableText>
            )}
            <SuggestionBox
              isShowingModal={isShowingSuggestionBox}
              setIsShowingModal={setIsShowingSuggestionBox}
              suggestionType={SuggestionTypes.dataTransformUtility}
              labelText={messages.dataTransformNotSeeingAction}
            />
          </StyledActionUnavailable>
        )}
      </StyledContents>
    </StyledDataTransformContainer>
  ) : (
    <StyledRow>
      <FormLabel>{intl.formatMessage(messages.dataTransformVarErrorMessage)}</FormLabel>
    </StyledRow>
  )
}
export default connect(DataTransformOperation)

export { getInitialValues, getValidationSchema, getDataTransformOperationHelpUrl }
